import UserIcon from 'components/icons/UserIcon';
import React, { ReactNode } from 'react';
import Container from './Container';
import NextLink from 'next/link';
import classnames from 'utils/classnames';
import AddressIcon from 'components/icons/AddressIcon';
import SubscriptionIcon from 'components/icons/SubscriptionIcon';
import OrderIcon from 'components/icons/OrderIcon';
import PasswordIcon from 'components/icons/PasswordIcon';
import LogOutIcon from 'components/icons/LogOutIcon';
import sanfordApi, { SFCustomer } from 'lib/sanford-api';
import { useCustomer } from 'hooks/useCustomer';
import Router, { useRouter } from 'next/router';
import { QUERIES } from 'queries';
import { useQueryClient } from 'react-query';
import UserIconLg from 'components/icons/UserIconLg';
import DropdownMenu from 'components/ui/DropdownMenu';

type MenuItemProps = {
  icon: ReactNode;
  url: string;
  children?: React.ReactNode;
};

const MenuItem: React.FC<MenuItemProps> = (props) => {
  const { icon, url } = props;
  const router = useRouter();
  const isActive = router.pathname === url;

  return (
    <NextLink href={url} passHref>
      <a
        className={classnames(
          'flex items-center gap-3 border-b-2 border-sf-blue-700 py-3 font-medium uppercase transition-all duration-150',
          {
            'border-opacity-100 opacity-100': isActive,
            'border-opacity-0 opacity-30 hover:border-opacity-100 hover:opacity-40': !isActive,
          }
        )}
      >
        <div className="flex w-8 justify-center">{icon && icon}</div>

        {props.children}
      </a>
    </NextLink>
  );
};

type AccountProps = {
  customer: SFCustomer;
};

const Account: React.FC<{ children?: React.ReactNode }> = (props) => {
  const links = [
    {
      label: 'My Details',
      url: '/my-account/details',
      icon: <UserIcon />,
    },
    {
      label: 'Addresses',
      url: '/my-account/addresses',
      icon: <AddressIcon />,
    },
    {
      label: 'Subscriptions',
      url: '/my-account/subscriptions',
      icon: <SubscriptionIcon />,
    },
    {
      label: 'Orders',
      url: '/my-account/orders',
      icon: <OrderIcon />,
    },
    {
      label: 'Password',
      url: '/my-account/password',
      icon: <PasswordIcon />,
    },
  ];

  const currentLink = links.find((link) => Router.pathname.startsWith(link.url));

  const { customer } = useCustomer();
  const queryClient = useQueryClient();
  const logout = () => {
    sanfordApi
      .logout()
      .then((resp) => {
        if (resp.data.success) {
          Router.push('/');
          queryClient.invalidateQueries([QUERIES.CUSTOMER]);
        }
      })
      .catch((error) => {
        alert('ERROR');
      });
  };
  return (
    <Container>
      <div className="grid grid-cols-4 gap-6 sm:grid-cols-12">
        <div className="col-span-4 sm:col-span-5 md:col-span-4 md:col-start-2 xl:col-span-3 xl:col-start-3">
          <div className="mb-12 flex items-center gap-4 text-sf-blue-700">
            <div className="w-16 flex-none rounded-full bg-sf-gold-200 p-2 text-sf-blue-500">
              <UserIconLg />
            </div>
            <div className="flex flex-col overflow-hidden">
              <span className="mb-2 text-xl font-medium">Hello, {customer.firstName}</span>
              <span title={customer.email} className="block overflow-hidden text-ellipsis text-sm">
                {customer.email}
              </span>
            </div>
          </div>
          <div className="md:hidden">
            <DropdownMenu
              current={
                <div className="flex items-center gap-3 py-3">
                  {currentLink.icon} {currentLink.label}
                </div>
              }
            >
              {links.map((link) => (
                <MenuItem key={link.url} icon={link.icon} url={link.url}>
                  {link.label}
                </MenuItem>
              ))}
              <button
                onClick={logout}
                className={classnames(
                  'flex w-full items-center gap-3 border-b-2 border-sf-blue-700 border-opacity-0 py-3 font-medium uppercase opacity-30 transition-all duration-150 hover:border-opacity-100 hover:opacity-100'
                )}
              >
                <LogOutIcon />
                Logout
              </button>
            </DropdownMenu>
          </div>
          <div className="items-strech hidden flex-col gap-2 font-medium text-sf-blue-700 md:flex">
            {links.map((link) => (
              <MenuItem key={link.url} icon={link.icon} url={link.url}>
                {link.label}
              </MenuItem>
            ))}
            <button
              onClick={logout}
              className={classnames(
                'flex items-center gap-3 border-b-2 border-sf-blue-700 border-opacity-0 py-3 font-medium uppercase opacity-30 transition-all duration-150 hover:border-opacity-100 hover:opacity-100'
              )}
            >
              <LogOutIcon />
              Logout
            </button>
          </div>
        </div>
        <div className="col-span-4 mb-12 sm:col-span-7 md:col-span-6 xl:col-span-5">
          {props.children}
        </div>
      </div>
    </Container>
  );
};

export default Account;
